import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  apiErrorMessage,
  CancellationPolicy,
  TravelBooking,
  useCancelBooking,
  useCancellationPolicy,
  useTravelBookingRefresh,
} from './queries';
import {
  NO_ENQUIRY,
  enquiryFailed,
  enquiryOf,
  enquiryResolved,
  enquiryStarted,
  outcomeAppliesToScreen,
  shouldRefreshCancellationPolicyOnOpen,
} from './cancellationEnquiry';

export interface BookingCancellationParams {
  /** La reserva que se está mirando. */
  booking: TravelBooking | null;
  /**
   * Preguntarle la política al proveedor tiene sentido: el conector NO dijo
   * que no la publica. Con un "no" explícito no se pregunta y el diálogo lo
   * dice — pedirla igual devuelve un error que se leería como falla nuestra.
   */
  enquire: boolean;
  /**
   * Dónde aplicar la fila autoritativa. El cancel devuelve la reserva
   * actualizada —estado y precio vigente— y es ESA la que se pinta: la de
   * antes ya no describe nada.
   */
  onBooking: (booking: TravelBooking) => void;
}

export interface BookingCancellation {
  /** La política DE ESTA reserva, o nada. Nunca la de otra. */
  policy: CancellationPolicy | null;
  /**
   * Ya se le preguntó al proveedor por esta reserva (o se abrió el diálogo,
   * que pregunta). Sin eso no hay nada que afirmar: dibujar "no publica
   * política" sin haberla pedido sería inventar una respuesta que nadie dio.
   */
  asked: boolean;
  /** Por qué no hay política (texto del backend o del proveedor). */
  notice: string | null;
  isLoadingPolicy: boolean;
  isCancelling: boolean;
  isOpen: boolean;
  /** Por qué el operador NO dejó cancelar. */
  refusal: string | null;
  /** Trae la política sin abrir el diálogo. */
  enquirePolicy: () => Promise<void>;
  /** Abre el diálogo y pregunta: cancelar nunca es un click directo. */
  ask: () => void;
  close: () => void;
  confirm: () => Promise<void>;
  /** Todo a cero: la política de una reserva no describe a la siguiente. */
  reset: () => void;
}

/**
 * El momento de CANCELAR, idéntico por donde entre el asesor (el modal del
 * flujo y el detalle del listado).
 *
 * Cancelar no es un click directo: primero se le pregunta al proveedor cuánto
 * cuesta hoy y qué se devuelve, y recién con eso en pantalla se ofrece el
 * botón rojo (`cancellationPolicy.ts` decide qué se dibuja y si el botón
 * puede ejecutar). Lo que devuelve el cancel es la fila AUTORITATIVA —estado
 * y precio vigente— y es la que se pinta.
 *
 * TODO lo que se muestra está atado a la reserva que lo produjo
 * (`cancellationEnquiry.ts`): la pantalla cambia de reserva sin desmontarse,
 * y una política —o un diálogo abierto, o el motivo de un rechazo— que
 * sobreviva a ese cambio es un cargo ajeno delante de una acción
 * irreversible.
 *
 * Un rechazo del operador (tarifa emitida, ticket no reembolsable) NO cierra
 * el diálogo: no es que se rompió algo, es que no se puede, y el asesor tiene
 * que poder leer el motivo para explicárselo al pasajero.
 */
export function useBookingCancellation({
  booking,
  enquire,
  onBooking,
}: BookingCancellationParams): BookingCancellation {
  const { t } = useTranslation();
  const cancel = useCancelBooking();
  const policyQuery = useCancellationPolicy();
  const refreshBooking = useTravelBookingRefresh();

  const [enquiry, setEnquiry] = useState(NO_ENQUIRY);
  /** De qué reserva está abierto el diálogo. */
  const [openFor, setOpenFor] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<
    { bookingId: string; message: string } | null
  >(null);

  const bookingId = booking?.id;
  const own = enquiryOf(enquiry, bookingId);
  /**
   * En qué reserva está la pantalla AHORA. Una operación en vuelo se resuelve
   * con el `bookingId` que capturó al empezar; para saber si ese resultado
   * todavía es de esta pantalla hace falta el valor vigente, no el de la
   * clausura que la lanzó.
   */
  const screenBookingId = useRef(bookingId);
  screenBookingId.current = bookingId;

  const enquirePolicy = useCallback(async () => {
    if (!bookingId) return;
    // La identidad se fija ACÁ: lo que vuelva se aplica solo si sigue siendo
    // esta consulta, de esta reserva.
    const target = bookingId;
    setEnquiry(enquiryStarted(target, enquire));
    if (!enquire) return;
    try {
      const policy = await policyQuery.mutateAsync(target);
      setEnquiry((state) => enquiryResolved(state, target, policy));
    } catch (error) {
      // El motivo va donde el asesor está mirando, no a un toast que se va
      // solo: sin él el diálogo diría "no publica" sobre un proveedor que sí
      // publica y no pudimos consultar.
      const notice =
        apiErrorMessage(error) ?? t('travelServices.detail.policyUnknown');
      setEnquiry((state) => enquiryFailed(state, target, notice));
    }
  }, [bookingId, enquire, policyQuery, t]);

  const ask = useCallback(() => {
    if (!bookingId) return;
    setRefusal(null);
    setOpenFor(bookingId);
    // La consulta arranca en el MISMO gesto que abre el diálogo: así el
    // diálogo nace esperando la respuesta y su botón rojo no puede ejecutar
    // en el hueco entre abrirse y preguntar.
    if (!shouldRefreshCancellationPolicyOnOpen(enquiry, bookingId)) return;
    void enquirePolicy();
  }, [bookingId, enquirePolicy, enquiry]);

  const close = useCallback(() => {
    setOpenFor(null);
    setRefusal(null);
  }, []);

  const confirm = useCallback(async () => {
    if (!bookingId) return;
    // El intento entero queda atado a ESTA reserva: si la pantalla se mueve
    // mientras el cancel está en vuelo, lo que vuelva no la toca (ni la fila,
    // ni el diálogo que el asesor pueda haber abierto para otra, ni el motivo
    // de un rechazo). La operación ya ocurrió del lado del hub y el listado se
    // invalida igual: no se pierde nada, se deja de pintar lo ajeno.
    const target = bookingId;
    const applyToScreen = (updated: TravelBooking) => {
      if (!outcomeAppliesToScreen(target, screenBookingId.current)) return;
      onBooking(updated);
    };
    setRefusal(null);
    try {
      const updated = await cancel.mutateAsync(target);
      applyToScreen(updated);
      if (!outcomeAppliesToScreen(target, screenBookingId.current)) return;
      setOpenFor(null);
    } catch (error) {
      const message =
        apiErrorMessage(error) ?? t('travelServices.booking.cancelError');
      // La reserva sigue viva: se relee para que la pantalla muestre su
      // estado real, no el que se quiso dejar. La relectura invalida el
      // listado aunque su resultado ya no sea de esta pantalla.
      const refresh = refreshBooking(target, applyToScreen);
      if (outcomeAppliesToScreen(target, screenBookingId.current)) {
        setRefusal({ bookingId: target, message });
      }
      await refresh;
    }
  }, [bookingId, cancel, onBooking, refreshBooking, t]);

  const reset = useCallback(() => {
    setEnquiry(NO_ENQUIRY);
    setOpenFor(null);
    setRefusal(null);
  }, []);

  return {
    policy: own.policy,
    asked: own.asked,
    notice: own.notice,
    isLoadingPolicy: own.loading,
    isCancelling: cancel.isLoading,
    isOpen: Boolean(bookingId) && openFor === bookingId,
    refusal:
      refusal && refusal.bookingId === bookingId ? refusal.message : null,
    enquirePolicy,
    ask,
    close,
    confirm,
    reset,
  };
}
